import React, { useEffect, useMemo } from 'react';
import {
    ActType,
    IMultiCapabilityTemplateInfo,
    ITemplateComponentProps,
    OPERATE_TYPE,
    PanelType,
    TemplateType,
} from '../../../interface/multiCapabilityTemplate';
import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components';
import { Button, Radio, RadioGroupTabs } from '@umu/umu-design';
import { defaultListData, templateTypeMapInfo, templateTypeTabList } from '../../../dataFlow/multiCapabilityTemplate';
import { TemplateTable } from './templateTable';
import dayjs from 'dayjs';
import { onDeleteTemplate, operateBtnList } from '../../dataFlow';
import { renderTooltip } from '../../dataFlow/element';
import { isMock } from '../../../dataFlow';
const defaultDateFormat = 'YYYY-MM-DD'; // 日期选择器默认日期格式

const BodyContent = (props: ITemplateComponentProps) => {
    const {
        templateType,
        isLoading,
        dataSource: { listData, listSelectInfo },
        onCommunication,
    } = props;
    const { list, page_info } = listData[templateType] || defaultListData;
    const onTemplateTypeClick = (_e: any, _index: number, item: any) => {
        onCommunication(ActType.UPDATE_TEMPLATE_TYPE, { info: { templateType: item.value } });
    };
    const onPanelChange = (item: PanelType, data?: IMultiCapabilityTemplateInfo) => {
        onCommunication(ActType.UPDATE_PANEL_TYPE, {
            isRequireTemplateList: item !== PanelType.ADD,
            info: {
                panelType: item,
                isShowBack: true,
                previewPanelType: [PanelType.LIST],
                templateInfo: data,
            },
        });
    };
    const onSelectTemplate = (info: IMultiCapabilityTemplateInfo) => {
        onCommunication(ActType.UPDATE_TEMPLATE_SELECT, { info: { templateType, info } });
    };
    const onOperateSuccess = (isSuccess: boolean) => {
        const page = listData[templateType]?.page_info?.current_page || 1;
        isSuccess && onCommunication(ActType.UPDATE_TEMPLATE_LIST, { info: { page } });
    };
    const onOperateBtnClick = (key: OPERATE_TYPE, record: IMultiCapabilityTemplateInfo) => {
        switch (key) {
            case OPERATE_TYPE.VIEW:
                onPanelChange(PanelType.PREVIEW, record);
                break;
            case OPERATE_TYPE.EDIT:
                onPanelChange(PanelType.EDIT, record);
                break;
            case OPERATE_TYPE.DELETE:
                onDeleteTemplate(templateType, record, { callback: onOperateSuccess });
                break;
            default:
                break;
        }
    };
    const btnList = useMemo(() => {
        return operateBtnList.filter((item) => item.showList.includes(templateType));
    }, [templateType]);

    const getColumns = () => {
        return [
            {
                title: ' ',
                dataIndex: 'id',
                width: 30,
                render: (id: string, record: IMultiCapabilityTemplateInfo) => {
                    const option = [
                        {
                            label: '',
                            key: id,
                        },
                    ];
                    return (
                        <Radio
                            active={listSelectInfo?.info.id}
                            list={option}
                            onChange={() => onSelectTemplate(record)}
                        ></Radio>
                    );
                },
            },
            {
                title: lang('模板名称'),
                dataIndex: 'title',
                width: 100,
                render: renderTooltip,
            },
            {
                title: lang('创建时间'),
                width: 100,
                hidden: [TemplateType.ENTERPRISE, TemplateType.UMU].includes(templateType),
                dataIndex: 'create_ts',
                render: (text: number) => {
                    return text ? dayjs(Number(text) * 1000).format(defaultDateFormat) : '-';
                },
            },
            {
                title: lang('描述'),
                width: 200,
                dataIndex: 'desc',
                render: renderTooltip,
            },
            {
                title: lang('操作'),
                dataIndex: 'operate',
                width: btnList.length * 40,
                render: (_text: string, record: IMultiCapabilityTemplateInfo) => {
                    return (
                        <div className="operate-btn-list">
                            {btnList.map((item, index) => {
                                return (
                                    <Button
                                        key={item.key}
                                        type="text"
                                        className={index !== 0 ? 'btn-split' : ''}
                                        isOnlyChildren
                                        onClick={() => onOperateBtnClick(item.key, record)}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                        </div>
                    );
                },
            },
        ].filter((item) => !item?.hidden);
    };
    // 记得删除
    useEffect(() => {
        if (isMock) {
            if (list && list[0]) {
                onOperateBtnClick(OPERATE_TYPE.EDIT, list[0]);
            }
        }
    }, [list]);
    return (
        <div className="template-page-list">
            <div className="desc-info">
                {lang('点评者将根据你提供的多维度评价对学员进行评价，使用企业模板可沉淀多维度评价数据')}
            </div>
            <div className="template-tabs-list">
                <RadioGroupTabs items={templateTypeTabList} onClick={onTemplateTypeClick} selected={templateType} />
                <If condition={[TemplateType.MY].includes(templateType)}>
                    <Button type="ghost-blue" iconName="AddSmall" onClick={() => onPanelChange(PanelType.ADD)}>
                        {lang('新建评价标准_New_scorecard')}
                    </Button>
                </If>
            </div>

            <TemplateTable
                columns={getColumns()}
                list={list}
                isLoading={isLoading}
                emptyText={templateTypeMapInfo[templateType].emptyText}
                pageInfo={page_info}
                onPageChange={(page) => onCommunication(ActType.UPDATE_TEMPLATE_LIST, { info: { page } })}
            />
        </div>
    );
};
const FooterContent = (props: ITemplateComponentProps) => {
    const {
        templateType,
        dataSource: { listSelectInfo },
        onCommunication,
    } = props;
    const [isNext, primaryText] = useMemo(() => {
        const isNext = [TemplateType.ENTERPRISE].includes(templateType);
        return [isNext, isNext ? lang('下一步') : lang('确认')];
    }, [templateType]);
    const onPrimaryClick = () => {
        if (isNext) {
            // 企业模版有对应的下一步操作，后续有其他类型在进行map扩展
            onCommunication(ActType.UPDATE_PANEL_TYPE, {
                info: {
                    panelType: PanelType.SELECT_CAPABILITY_DETAIL,
                    isShowBack: true,
                    previewPanelType: [PanelType.LIST],
                },
            });
        } else {
            onCommunication(ActType.PANEL_SUBMIT, { info: {} });
        }
    };
    const onCancel = () => {
        onCommunication(ActType.PANEL_CANCEL, { info: {} });
    };
    const primaryAllowOperate = listSelectInfo?.templateType === templateType && !!listSelectInfo?.info?.id;
    return (
        <div className="footer-list capability-flex-col fix-right">
            <Button type="ghost-grey" onClick={onCancel}>
                {lang('取消')}
            </Button>
            <Button type="primary" disabled={!primaryAllowOperate} className="btn-split" onClick={onPrimaryClick}>
                {primaryText}
            </Button>
        </div>
    );
};
export const templateList = {
    BodyContent,
    FooterContent,
};
